import type {
    AccountSummary,
    CategorySummary,
    HolisticSummary,
} from '@fruitful/types';
import { atom } from 'jotai';

import { FRUITFUL_INSTITUTION_NAME } from '../../banking/types/BankAccount';
import { StorageKeys } from '../../common/constants';
import { atomWithStorage } from '../../common/providers/StorageProvider';
import { type HolisticSummaryCache } from '../types/HolisticSummary';

export const isLoadingHolisticSummaryAtom = atom<boolean | null>(null); // init to null, which means not started
export const holisticSummaryAtom = atom<HolisticSummary | null>(null);
export const holisticSummaryCacheAtom =
    atomWithStorage<HolisticSummaryCache | null>(
        StorageKeys.HOLISTIC_SUMMARY_CACHE,
        null,
    );

// A derived atom, which is a flattened list of all accounts within holisticSummaryAtom
export const holisticAccountsAtom = atom<AccountSummary[]>((get) => {
    const holisticSummary = get(holisticSummaryAtom);
    return Object.keys(holisticSummary ?? {})
        .map((key) => (holisticSummary?.[key] as CategorySummary).accounts)
        .flat();
});
// A derived atom, used to get external accounts
export const externalHolisticAccountsAtom = atom<AccountSummary[]>((get) => {
    return get(holisticAccountsAtom).filter(
        (acc) => acc.institutionName !== FRUITFUL_INSTITUTION_NAME,
    );
});
// A derived atom, used to know if user has linked accounts beyond their Fruitful accounts
export const hasAnyExternalHolisticAccountsAtom = atom<boolean>((get) => {
    return (
        get(holisticAccountsAtom).filter(
            (acc) => acc.institutionName !== FRUITFUL_INSTITUTION_NAME,
        ).length > 0
    );
});

type VoidFunc = () => void;

export const hasAcceptedInstitutionLinkDisclosuresAtom = atom(false); // Do we need to show the disclosures screen
export const isShowingInstitutionLinkDisclosuresAtom = atom(false); // Is the disclosures screen/modal open
export const isInstitutionLinkingInProgressAtom = atom(false); // Is the institution linking process currently in progress
export const isQuilttConnectorOpenAtom = atom(false); // Is the Quiltt connector currently open - mostly used to prevent inactivity timer from logging the user out
export const onQuilttConnectorExitAtom = atom<VoidFunc | null>(null); // Optional function to execute on exiting quiltt connector
export const isPartnerMembershipAtom = atom(false); // Storing this fact here for easy usage in account linking disclosures modal

export const selectedConnectionIdAtom = atom<string | null>(null); // used for showing accounts after successfully linking with quiltt
export const matchedHolisticAccountsAtom = atom<AccountSummary[]>([]); // used for showing accounts after successfully linking with quiltt
export const selectedHolisticAccountAtom = atom<AccountSummary | null>(null); // used for editing account details after successfully linking with quiltt

export const quilttSessionTokenAtom = atom<string | null>(null); // used for storing the quiltt session token
